import type { BaseSyntheticEvent, MouseEvent, KeyboardEvent } from 'react';

import { Text, Stack } from '@backyard-ui/core';
import type {
  AutocompleteCollection,
  AutocompleteApi,
} from '@algolia/autocomplete-core';

import type { AutocompleteItem } from '../../Searchbar.types';
import { formatPrice, normalizeItemPricing } from '../../Searchbar.utilts';
import { getProductPageUrl } from '../ServicesSearchbar.utils';

import styles from '../ServicesSearchbar.styles';
import { useHeaderStore } from '../../../../../Header.store';
import ServiceProductImage from './ServiceProductImage';

interface ServiceListProps
  extends AutocompleteCollection<AutocompleteItem>,
    Partial<
      AutocompleteApi<
        AutocompleteItem,
        BaseSyntheticEvent,
        MouseEvent,
        KeyboardEvent
      >
    > {
  storeSlug?: string;
}

function ServiceList(props: ServiceListProps) {
  const { source, items, getItemProps, storeSlug } = props;

  const region = useHeaderStore((state) => state.locationContext.region);

  return (
    <li
      key={source.sourceId}
      className={styles().gridServices()}
      data-area="services"
    >
      <Text size="lg" weight="bold" asChild>
        <span>Serviços relacionados</span>
      </Text>

      <ul className={styles().list()}>
        {items.map((item) => {
          const pricing = normalizeItemPricing(item, region, storeSlug);

          const offer = pricing?.offer;
          const discount = pricing?.discount;
          const price = pricing?.originalPrice;
          const promotional = pricing?.promotionalPrice;

          const isDFP =
            offer?.enabled === true &&
            discount?.discountType === 'payment-method' &&
            discount?.featureInstallmentDiscount === false;

          const isStampedPrice =
            offer?.enabled === true &&
            discount?.discountType === '' &&
            discount?.featureInstallmentDiscount === false;

          const isPromotional = isDFP || isStampedPrice;
          const showUnit = item.unit !== 'cada';

          return (
            <li
              key={item.objectID}
              className={styles().product()}
              {...getItemProps?.({
                item,
                source,
              })}
            >
              <a href={getProductPageUrl(item.url)}>
                <Stack spacing={2}>
                  <div className={styles().picture()}>
                    <ServiceProductImage
                      src={item.pictures?.normal}
                      alt={item.name}
                    />
                  </div>
                  <Stack direction="column" spacing={0}>
                    <Text size="md" noOfLines={2} asChild>
                      <span className="break-normal">{item.name}</span>
                    </Text>

                    {isPromotional && price != null && (
                      <Text
                        size="md"
                        color="neutral-900"
                        decoration="line-through"
                        asChild
                      >
                        <span>
                          {formatPrice(price)} {showUnit && item.unit}
                        </span>
                      </Text>
                    )}

                    {promotional != null && (
                      <Stack spacing="2" align="baseline">
                        <Text
                          color={isPromotional ? 'red-600' : 'neutral-900'}
                          weight="bold"
                          asChild
                        >
                          <span>
                            {formatPrice(promotional)} {showUnit && item.unit}
                          </span>
                        </Text>
                        {discount?.paymentMethodsDiscount && (
                          <Text
                            size="md"
                            color="gray-600"
                            weight="bold"
                            asChild
                          >
                            <span>{discount.paymentMethodsDiscount}</span>
                          </Text>
                        )}
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </a>
            </li>
          );
        })}
      </ul>
    </li>
  );
}

export default ServiceList;
